import React, { useState, useRef, useEffect } from "react";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Input } from "@shadcn-in-nx/ui/input";
import { X, Plus } from "lucide-react";

export default function TagInput({ tags = [], availableTags = [], onChange, disabled }) {
    const [inputValue, setInputValue] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Filter suggestions based on input
    const suggestions = availableTags
        .filter(tag => !tags.includes(tag) && tag.includes(inputValue.toLowerCase()))
        .slice(0, 5);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const addTag = (tag) => {
        const normalized = tag.trim().toLowerCase();
        if (normalized && !tags.includes(normalized)) {
            onChange([...tags, normalized]);
        }
        setInputValue("");
        setShowSuggestions(false);
    };

    const removeTag = (tagToRemove) => {
        onChange(tags.filter(tag => tag !== tagToRemove));
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && inputValue.trim()) {
            e.preventDefault();
            addTag(inputValue);
        } else if (e.key === "Backspace" && !inputValue && tags.length > 0) {
            removeTag(tags[tags.length - 1]);
        }
    };

    return (
        <div ref={containerRef} className="relative">
            <div className="flex flex-wrap gap-1.5 p-2 border rounded-md bg-background min-h-[38px]">
                {tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                        {tag}
                        {!disabled && (
                            <X className="w-3 h-3 cursor-pointer hover:text-destructive" onClick={() => removeTag(tag)} />
                        )}
                    </Badge>
                ))}
                {!disabled && (
                    <Input
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => { setInputValue(e.target.value); setShowSuggestions(true); }}
                        onFocus={() => setShowSuggestions(true)}
                        onKeyDown={handleKeyDown}
                        placeholder={tags.length === 0 ? "Add tags..." : ""}
                        className="flex-1 min-w-[80px] h-6 border-0 p-0 focus-visible:ring-0 text-sm"
                    />
                )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md">
                    {suggestions.map((tag) => (
                        <div
                            key={tag}
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-accent flex items-center gap-2"
                            onClick={() => addTag(tag)}
                        >
                            <Plus className="w-3 h-3" />
                            {tag}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
